import { addDays, addYears, isBefore, isValid, parse } from 'date-fns';
import { COSTA_RICA } from 'src/constants/countryCode';
import {
  isValidPersonDimex,
  isValidPersonId,
} from 'src/functions/identificationMethods';
import {
  boolean,
  number,
  object,
  ObjectSchema,
  string,
  StringSchema,
} from 'yup';

export const errorMessages = {
  nameNoNumbers: 'El nombre no puede contener números',
  nameRequired: 'El nombre es obligatorio',
  lastNameNoNumbers: 'El apellido no puede contener números',
  lastNameRequired: 'El apellido es obligatorio',
  genderRequired: 'El género es obligatorio',
  dateMalformed: 'La fecha debe tener formato DD/MM/AAAA',
  dateTooYoung: 'Debe ser mayor de 18 años',
  dateRequired: 'La fecha de nacimiento es obligatoria',
  emailRequired: 'El correo es obligatorio',
  emailMalformed: 'Formato de correo inválido',
  phoneRequired: 'El teléfono es obligatorio',
  phoneTooShort: 'El teléfono debe contener mínimo 8 caracteres',
  phoneMalformed: 'El teléfono solo puede contener números',
  phoneNotStartWithZero: 'El teléfono no puede iniciar con 0',
  phoneTooLong: 'El teléfono debe contener un máximo de 8 caracteres',
  entityName: 'El nombre de la empresa es obligatorio.',
  type: 'El tipo de cliente es obligatorio.',
  emptyIdNumber: 'El número de identificación es requerido',
  salaryRequired: 'El ingreso mensual debe ser un número entero',
  salaryNotDecimal: 'El ingreso mensual debe ser un número entero',
  salaryMoreThanZero: 'El ingreso mensual debe ser mayor que 0',
  addressDetailRequired: 'La dirección es requerida',
  addressDetailTooShort: 'La dirección debe contener al menos 10 caracteres',
  addressDetailTooLong: 'La dirección no debe superar los 250 caracteres',
  enterpriseActivityRequired:
    'La actividad de la empresa o negocio es requerida',
  originDetailRequired: 'El detalle de ingreso es requerido',
  originDetailTooShort:
    'El detalle de ingreso debe contener al menos 20 caracteres',
  originDetailTooLong:
    'El detalle de ingreso no debe superar los 200 caracteres',
};

const makeBirthdayValidationLogic = (
  dateFormat: string,
  maxDate: Date,
): StringSchema =>
  string()
    .test(
      'Check format',
      errorMessages.dateMalformed,
      (birthdayString: string): boolean => {
        const birthdate = parse(birthdayString, dateFormat, new Date());
        /**
         * a date string such as 12/12/1 gets parsed to Wed Dec 12 00001
         * thus we need to ensure the user is inserting full dd/mm/yyyy
         */
        return isValid(birthdate) && birthdayString.length === 10;
      },
    )
    .test(
      'Check date boundary',
      errorMessages.dateTooYoung,
      (birthdayString: string): boolean => {
        const birthdate = parse(birthdayString, dateFormat, new Date());
        return isBefore(birthdate, maxDate);
      },
    )
    .required(errorMessages.dateRequired);

export default function makeValidationSchema(
  isIndividualProfile: boolean,
  insuranceValue: number,
): ObjectSchema {
  const maxDate = addDays(addYears(Date.now(), -18), 1);
  const commonValidations = {
    idType: string(),
    idNumber: string()
      .when('idType', {
        is: 'N',
        then: string()
          .test(
            'Check id number format',
            'Formato de cédula inválida',
            isValidPersonId,
          )
          .required(errorMessages.emptyIdNumber),
      })
      .when('idType', {
        is: 'U',
        then: string()
          .test(
            'Check DIMEX number format',
            'Debe iniciar con 1 y contener 12 caracteres',
            isValidPersonDimex,
          )
          .required(errorMessages.emptyIdNumber)
          .max(12, 'Debe iniciar con 1 y contener 12 caracteres'),
      })
      .when('idType', {
        is: 'P',
        then: string().required(errorMessages.emptyIdNumber),
      }),
    names: string().required('El nombre es obligatorio'),
    lastNames: string().required('El apellido es obligatorio'),
    email: string()
      .required('El correo es requerido')
      .email('Formato de correo inválido'),
    phoneNumber: string()
      .required(errorMessages.phoneRequired)
      .min(8, errorMessages.phoneTooShort)
      .max(8, errorMessages.phoneTooLong)
      .matches(/^[0-9]*$/, {
        message: errorMessages.phoneMalformed,
      })
      .test(
        'no-initial-zero',
        errorMessages.phoneNotStartWithZero,
        (value) => !value || value?.charAt(0) !== '0',
      ),
    gender: string().required(errorMessages.genderRequired),
    effectiveDate: string().required(
      'La fecha de inicio de protección de tu seguro es requerida',
    ),
    birthday: makeBirthdayValidationLogic('dd/MM/yyyy', maxDate),
    addressCountry: string().required('El país es requerido'),
    addressTownship: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('El distrito es requerido'),
      otherwise: string().notRequired(),
    }),
    addressCity: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('El cantón es requerido'),
      otherwise: string().notRequired(),
    }),
    addressState: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('La provincia es requerida'),
      otherwise: string().notRequired(),
    }),
    addressDetail: string()
      .min(10, errorMessages.addressDetailTooShort)
      .max(250, errorMessages.addressDetailTooLong)
      .required(errorMessages.addressDetailRequired),
  };

  const collectiveValidation = {
    ...commonValidations,
    effectiveDate: string().required(
      'La fecha de inicio de protección de tu seguro es requerida',
    ),
  };

  const individualValidation = {
    ...commonValidations,
    originType: string().required('La procedencia de los fondos es requerida'),
    profession: string(),
    occupation: string().when('originType', {
      is: (value) => ['001', '003', '004'].includes(value),
      then: string().required('El cargo que desempeña es requerido'),
      otherwise: string().notRequired(),
    }),
    enterprise: string().when('originType', {
      is: (value) => ['001', '003', '004'].includes(value),
      then: string().required('El nombre de la empresa es requerido'),
      otherwise: string().notRequired(),
    }),
    salary: number()
      .typeError('El ingreso mensual es requerido')
      .moreThan(0, errorMessages.salaryMoreThanZero)
      .integer(errorMessages.salaryNotDecimal)
      .required(errorMessages.salaryRequired),
    typeOfMoney: string().required('El tipo de moneda es requerido'),
    originCountry: string().required(
      'El país de origen de los ingresos es requerido',
    ),
    pep: boolean().required('Favor especificar si es persona pep'),
    birthPlace: string().required('El país y lugar de nacimiento es requerido'),
    currentBank: string(),
    civilStatus: string(),
    creditAmount: number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .when(['currentBank'], {
        is: (currentBank) => currentBank !== '',
        then: number()
          .typeError('El valor digitado debe ser numérico')
          .required('Monto requerido'),
        otherwise: number()
          .transform((value) => (Number.isNaN(value) ? undefined : value))
          .notRequired(),
      })
      .max(
        insuranceValue,
        'El monto de acreencia no puede ser mayor al monto asegurado',
      ),
  };

  return object().shape(
    isIndividualProfile ? individualValidation : collectiveValidation,
  );
}
